/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment */
// sonar.issue.ignore.multicriteria=e1
// sonar.issue.ignore.multicriteria.e1.ruleKey=typescript:S4165
// sonar.issue.ignore.multicriteria.e1.resourceKey=**/*
import { colors } from './colors';

import type { SxProps, Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

export type CustomTypographyVariant =
	| 'bodyL'
	| 'bodyLBold'
	| 'bodyM'
	| 'bodyMSemiBold'
	| 'bodyS'
	| 'bodySSemiBold'
	| 'bodySBold';

export type CustomButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'primaryDefault';

export type CustomButtonSize = 'small' | 'medium' | 'large';

export const ui = {
	fontFamily: {
		body: "'Work Sans', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
		heading: "'Figtree', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
	},
	colors,
} as const;

const bodyCommon: SxProps<Theme> = {
	color: ui.colors.text.primary,
	fontFamily: ui.fontFamily.body,
	letterSpacing: '-0.5px',
	fontWeight: 400,
	textTransform: 'none',
};

const bodyL: SxProps<Theme> = {
	...bodyCommon,
	fontSize: '1rem',
	lineHeight: '1.5rem',
	'@media (min-width:600px)': { fontSize: '1.125rem', lineHeight: '1.5em' },
	'@media (min-width:960px)': { fontSize: '1.25rem', lineHeight: '1.75rem' },
};

const bodyM: SxProps<Theme> = {
	...bodyCommon,
	fontSize: '0.875rem',
	lineHeight: '1.25rem',
	'@media (min-width:600px)': { fontSize: '1rem', lineHeight: '1.5em' },
	'@media (min-width:960px)': { fontSize: '1.125rem', lineHeight: '1.5rem' },
};

const bodyS: SxProps<Theme> = {
	...bodyCommon,
	fontSize: '0.875rem',
	lineHeight: '1.25rem',
	'@media (min-width:960px)': { fontSize: '1rem', lineHeight: '1.5em' },
};

export const uiTypographySx: Record<CustomTypographyVariant, SxProps<Theme>> = {
	bodyL,
	bodyLBold: { ...bodyL, fontWeight: 600 },
	bodyM,
	bodyMSemiBold: { ...bodyM, fontWeight: 500 },
	bodyS,
	bodySSemiBold: { ...bodyS, fontWeight: 500 },
	bodySBold: {
		...bodyS,
		fontWeight: 600,
	},
};

const sharedButton: SxProps<Theme> = {
	textTransform: 'none',
	borderRadius: '42px',
	display: 'inline-flex',
	height: 'auto',
	flexShrink: 0,
	transition: 'background-color 0.2s, color 0.2s, border-color 0.2s',
};

const buttonL: SxProps<Theme> = {
	...sharedButton,
	padding: '18px 36px',
	'&.MuiButton-root': { ...uiTypographySx.bodyMSemiBold } as SystemStyleObject<Theme>,
	'& .MuiButton-startIcon': { marginRight: '0.875rem' },
	'& .MuiButton-endIcon': { marginLeft: '0.875rem' },
};

const buttonM: SxProps<Theme> = {
	...sharedButton,
	height: '3.25rem',
	padding: '14px 32px',
	'&.MuiButton-root': { ...uiTypographySx.bodyMSemiBold } as SystemStyleObject<Theme>,
	'& .MuiButton-startIcon': { marginRight: '0.75rem' },
	'& .MuiButton-endIcon': { marginLeft: '0.75rem' },
};

const buttonS: SxProps<Theme> = {
	...sharedButton,
	height: '2.75rem',
	padding: '10px 24px',
	'&.MuiButton-root': { ...uiTypographySx.bodySSemiBold } as SystemStyleObject<Theme>,
	'& .MuiButton-startIcon': { marginRight: '0.625rem' },
	'& .MuiButton-endIcon': { marginLeft: '0.625rem' },
};

const buttonXS: SxProps<Theme> = {
	...sharedButton,
	height: '2.75rem',
	padding: '6px 24px',
	'&.MuiButton-root': { ...uiTypographySx.bodySSemiBold } as SystemStyleObject<Theme>,
	'& .MuiButton-startIcon': { marginRight: '0.5rem' },
	'& .MuiButton-endIcon': { marginLeft: '0.5rem' },
};

export const uiButtonSx = {
	base: sharedButton,

	size: {
		large: {
			...buttonM,
			'@media (min-width:900px)': buttonL,
		} as SxProps<Theme>,
		medium: {
			...buttonS,
			'@media (min-width:900px)': buttonM,
		} as SxProps<Theme>,
		small: {
			...buttonXS,
			'@media (min-width:900px)': buttonS,
		} as SxProps<Theme>,
	} satisfies Record<CustomButtonSize, SxProps<Theme>>,

	variant: {
		primary: {
			backgroundColor: ui.colors.text.secondary,
			'&.MuiButton-root': { color: ui.colors.text.contrast },
			'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.80)' },
		} as SxProps<Theme>,

		secondary: {
			border: `1px solid ${ui.colors.borderGray}`,
			backgroundColor: ui.colors.white,
			'&.MuiButton-root': { color: ui.colors.text.secondary },
			'&:hover': { backgroundColor: ui.colors.borderGray },
		} as SxProps<Theme>,

		tertiary: {
			backgroundColor: 'transparent',
			border: 'none',
			'&.MuiButton-root': {
				color: ui.colors.text.secondary,
				paddingLeft: 0,
				paddingRight: 0,
			},
			'&:hover': { textDecoration: 'underline', backgroundColor: 'transparent' },
		} as SxProps<Theme>,

		primaryDefault: {
			backgroundColor: ui.colors.brand.primary,
			'&.MuiButton-root': { color: ui.colors.text.contrast },
			'&:hover': { backgroundColor: '#003C94' },
		} as SxProps<Theme>,
	} satisfies Record<CustomButtonVariant, SxProps<Theme>>,
} as const;

export const uiIconButtonSx = {
	base: {
		transition: 'background-color 0.2s, color 0.2s, border-color 0.2s',
		border: `1px solid ${ui.colors.borderGray}`,
		borderRadius: '50%',
		padding: '12px',
		color: ui.colors.dark,
		'&:hover': { backgroundColor: ui.colors.borderGray },
	} as SxProps<Theme>,
} as const;
